import * as React from "react";
import { Box, Chip, TextField, Typography } from "@mui/material";

// - standard emails
// - @{varName}
// - ${varName}
const EMAIL_OR_VAR_REGEX =
    /^(?:[^\s@]+@[^\s@]+\.[^\s@]{2,}|@\{[a-zA-Z_][\w.]*\}|\$\{[a-zA-Z_][\w.]*\})$/;

export default function MultiEmailInput({
    value,
    onChange,
    label = "Emails",
    placeholder = "Type an email and press Enter or comma…",
    helperText = "Use Enter or comma to add multiple emails",
    InputProps = {},
    variant = "outlined",
    allowDelete = true,
}) {
    const [input, setInput] = React.useState("");
    const emails = value ?? [];
    const setEmails = onChange ?? (() => { });

    const normalize = (s) => s.trim().replace(/,$/, "");

    const splitCandidates = (raw) =>
        raw
            .split(/[,\s]+/)
            .map(normalize)
            .filter(Boolean);

    const addEmails = (raw) => {
        const candidates = Array.isArray(raw) ? raw : splitCandidates(raw);
        if (!candidates.length) return;

        const currentSet = new Set(emails);
        const next = [...emails];
        for (const c of candidates) {
            if (!currentSet.has(c)) {
                next.push(c);
                currentSet.add(c);
            }
        }
        setEmails(next);
        setInput("");
    };

    const removeEmail = (toRemove) => {
        setEmails(emails.filter((e) => e !== toRemove));
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addEmails(input);
        } else if (e.key === "Backspace" && !input && emails.length) {
            removeEmail(emails[emails.length - 1]);
        }
    };

    const handlePaste = (e) => {
        const text = e.clipboardData.getData("text");
        if (text && /[, \n]/.test(text)) {
            e.preventDefault();
            addEmails(text);
        }
    };

    const invalidCount = emails.filter((e) => !EMAIL_OR_VAR_REGEX.test(e)).length;

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1,
                }}
            >
                {emails.map((email) => {
                    const valid = EMAIL_OR_VAR_REGEX.test(email);
                    return (
                        <Chip
                            key={email}
                            label={email}
                            disabled={!allowDelete}
                            onDelete={() => removeEmail(email)}
                            variant="outlined"
                            color={valid ? "default" : "error"}
                        />
                    );
                })}
            </Box>

            <TextField
                fullWidth
                label={label}
                placeholder={placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={() => addEmails(input)}
                InputProps={InputProps}
                variant={variant}
                helperText={
                    invalidCount
                        ? `${invalidCount} item${invalidCount > 1 ? "s" : ""} look incorrect. Invalid items will not be added.`
                        : helperText
                }
                error={Boolean(invalidCount)}
            />
        </Box>
    );
}
